import { type ReactNode, type CSSProperties, type JSX } from 'react';
import { ModalBodyFullScroll } from './modalBodyFullScroll';
import { ModalBodySectionedScroll } from './modalBodySectionedScroll';

export type BaseProps = {
  styles?: CSSProperties;
};

export type FullScrollProps = {
  /**
   * Defines the scrolling type for the modal body:
   * - 'full': The entire modal body is scrollable with specific styles applied.
   */
  scrollType?: 'full';
  children?: ReactNode;
  withFooter?: boolean;
  onRefChanged?: (element: HTMLDivElement) => void;

  fixedSection?: never; // Ensures fixedSection is not used
  scrollableSection?: never; // Ensures scrollableSection is not used
};

export type SectionedScrollProps = {
  /**
   * Defines the scrolling type for the modal body:
   * - 'sectioned': The modal body contains a fixed section and a scrollable section.
   *    - If there is enough space, only the scrollable section can be scrolled.
   *    - If the modal body height is too small, the scrollable section becomes fixed, and the entire modal body scrolls instead.
   */
  scrollType: 'sectioned';
  fixedSection?: ReactNode;
  scrollableSection?: ReactNode;

  onRefChanged?: never; // Ensures onRefChanged is not used
  children?: never; // Ensures children is not used
  withFooter?: never; // Ensures withFooter is not used
};

type ModalBodyProps = BaseProps & (FullScrollProps | SectionedScrollProps);

/**
 * The scrollable content area of the Modal. Has to be placed within `<ModalContent>`
 */
export const ModalBody = ({
  scrollType = 'full',
  children,
  fixedSection,
  scrollableSection,
  styles,
  withFooter,
  onRefChanged,
}: ModalBodyProps): JSX.Element =>
  scrollType === 'full' ? (
    <ModalBodyFullScroll
      styles={styles}
      withFooter={withFooter}
      onRefChanged={onRefChanged}
    >
      {children}
    </ModalBodyFullScroll>
  ) : (
    <ModalBodySectionedScroll
      styles={styles}
      fixedSection={fixedSection}
      scrollableSection={scrollableSection}
    />
  );
